import { useState, useEffect } from "react";
import { Bell, BellRing, X, Heart, MessageCircle, Star, Shield } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "jjodi_notif_prompt_dismissed";

/**
 * NotificationPrompt
 * Shows a beautiful custom popup asking user to allow notifications.
 * - Only shown when Notification.permission === "default" (undecided)
 * - Once user acts (allow/dismiss), we store in localStorage so it doesn't show again
 * - On "Allow": triggers browser's native permission request → then registers FCM token
 */
export default function NotificationPrompt({ user }) {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState("idle"); // idle | loading | success | denied

  useEffect(() => {
    if (!user) return;
    if (!("Notification" in window)) return;

    // Already decided by browser
    if (Notification.permission !== "default") return;

    // User already dismissed our custom prompt
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    // Show after a short delay so user settles on the page first
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, [user]);

  const handleAllow = async () => {
    setState("loading");
    try {
      const { requestFcmToken, setupForegroundNotifications } = await import("../lib/firebase");
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        setState("success");
        const token = await requestFcmToken();
        setupForegroundNotifications();
        if (token) {
          api.post("/notifications/save-token", { token }).catch(() => {});
        }
        // Auto-close after 2s on success
        setTimeout(() => {
          setVisible(false);
          localStorage.setItem(STORAGE_KEY, "1");
        }, 2000);
      } else {
        setState("denied");
        setTimeout(() => {
          setVisible(false);
          localStorage.setItem(STORAGE_KEY, "1");
        }, 2500);
      }
    } catch (err) {
      console.error("NotificationPrompt error:", err);
      setState("idle");
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
        onClick={handleDismiss}
        aria-hidden="true"
      />

      {/* Popup Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Enable Notifications"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] w-[92vw] max-w-sm
                   bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-border/60
                   overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-400"
      >
        {/* Top gradient banner */}
        <div className="relative h-28 bg-gradient-to-br from-[#6b1a24] via-[#9b2c3a] to-[#c0392b] flex items-center justify-center overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -left-6 w-24 h-24 rounded-full bg-white/10" />

          {/* Bell icon with ring animation */}
          <div className="relative z-10 w-16 h-16 rounded-full bg-white/20 flex items-center justify-center ring-4 ring-white/20 shadow-lg">
            {state === "success" ? (
              <span className="text-3xl animate-bounce">🎉</span>
            ) : state === "denied" ? (
              <span className="text-3xl">😔</span>
            ) : (
              <BellRing className="w-8 h-8 text-white animate-[wiggle_1s_ease-in-out_infinite]" />
            )}
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {state === "success" ? (
            <div className="text-center py-2">
              <p className="text-lg font-bold text-emerald-600">Notifications enabled! 🎊</p>
              <p className="text-sm text-muted-foreground mt-1">आप अब सभी updates तुरंत पाएंगे।</p>
            </div>
          ) : state === "denied" ? (
            <div className="text-center py-2">
              <p className="text-base font-semibold text-foreground">Permission नहीं मिली</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                Browser settings से notifications enable करें:<br />
                <span className="font-medium text-primary">Settings → Notifications → Allow</span>
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-foreground text-center leading-snug">
                Notifications Allow करें 🔔
              </h2>
              <p className="text-xs text-muted-foreground text-center mt-1.5 leading-relaxed">
                ताकि आप कोई भी जरूरी update miss न करें
              </p>

              {/* Feature list */}
              <div className="mt-4 space-y-2.5">
                <FeatureRow icon={<Heart className="w-4 h-4 text-rose-500" />} text="नए Interests और Connections की तुरंत जानकारी" />
                <FeatureRow icon={<MessageCircle className="w-4 h-4 text-blue-500" />} text="New Messages का real-time alert" />
                <FeatureRow icon={<Star className="w-4 h-4 text-amber-500" />} text="AI Matches और Profile views की सूचना" />
                <FeatureRow icon={<Shield className="w-4 h-4 text-emerald-500" />} text="कोई spam नहीं — सिर्फ जरूरी updates" />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-5">
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-2.5 rounded-2xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors font-medium"
                >
                  अभी नहीं
                </button>
                <button
                  onClick={handleAllow}
                  disabled={state === "loading"}
                  className="flex-1 py-2.5 rounded-2xl text-sm font-bold text-white
                             bg-gradient-to-r from-[#6b1a24] to-[#c0392b]
                             hover:from-[#7f1d2a] hover:to-[#c0392b]
                             disabled:opacity-70 transition-all shadow-md hover:shadow-lg
                             flex items-center justify-center gap-2"
                >
                  {state === "loading" ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Allow हो रहा है...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      Allow करें
                    </>
                  )}
                </button>
              </div>

              <p className="text-center text-[10px] text-muted-foreground mt-3">
                आप कभी भी browser settings से बंद कर सकते हैं
              </p>
            </>
          )}
        </div>
      </div>

      {/* Wiggle keyframe */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(-8deg); }
          50% { transform: rotate(8deg); }
        }
      `}</style>
    </>
  );
}

function FeatureRow({ icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
        {icon}
      </div>
      <p className="text-[12px] text-foreground leading-snug">{text}</p>
    </div>
  );
}
